/* eslint-disable @typescript-eslint/init-declarations */
/* eslint-disable no-eq-null */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { css } from "@emotion/css";
import { Callout } from "@octopusdeploy/design-system-components";
import { colorScales, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ActionTemplateSearchResource, ChannelResource, DeploymentActionResource, DeploymentSettingsResource, EnvironmentV2Resource, GitRefResource, ProjectResource, ResourcesById } from "@octopusdeploy/octopus-server-client";
import { SpecialVariables, StartTrigger } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { flatten } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import type { Location } from "react-router-dom";
import ProcessTemplateOnboarding from "~/areas/platformHub/processEditor/ProcessTemplateOnboarding";
import ProjectTemplateOnboarding from "~/areas/platformHub/processEditor/ProjectTemplatesOnboarding";
import { isMatchingFoundProcessTemplateDetails } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import type { ProcessStateSelectors } from "~/areas/projects/components/Process/Contexts/ProcessContextState";
import type { ProcessTemplateDetails } from "~/areas/projects/components/Process/Contexts/ProcessController";
import { useProcessTemplatesFromContext } from "~/areas/projects/components/Process/Contexts/ProcessTemplatesContextProvider";
import { ProcessTemplateStepSummary, getProcessTemplateStepDetailsUrl } from "~/areas/projects/components/Process/ListItems/ProcessListPageListItemForProcessTemplate";
import { getProcessTemplateSlugFromAction, getProcessTemplateVersionMaskFromAction } from "~/areas/projects/components/Process/ProcessTemplates/processTemplateId";
import type { DeploymentProcessIdentifier, ProcessIdentifier, StoredAction, StoredStep } from "~/areas/projects/components/Process/types";
import { isProjectTemplateProcessIdentifier, isProcessTemplateProcessIdentifier, isDeploymentProcessIdentifier, isTemplatedProjectProcessIdentifier } from "~/areas/projects/components/Process/types";
import { SampleProjectTourContext } from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTour";
import { SampleProjectTourStep } from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTourStep";
import getActionLogoUrl from "~/areas/projects/components/getActionLogoUrl";
import { useProjectContext } from "~/areas/projects/context";
import { repository } from "~/clientInstance";
import Roles from "~/components/Actions/Roles";
import AdvancedFilterLayout from "~/components/AdvancedFilterLayout/AdvancedFilterLayout";
import type { DoBusyTask } from "~/components/DataBaseComponent";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent";
import { NoResults } from "~/components/Images/NoResults/NoResults";
import { StepRolling } from "~/components/Images/Process/StepRolling";
import Markdown from "~/components/Markdown/index";
import InternalLink from "~/components/Navigation/InternalLink";
import { UnstructuredFormSection } from "~/components/form";
import { isVersioningConfigurationValid } from "../DeploymentProcessSettings/InvalidConfigurationCallout";
import RunbookOnboarding from "../Runbooks/RunbookOnboarding";
import Onboarding from "./Common/Onboarding";
import { useActionTemplatesFromContext } from "./Contexts/ProcessActionTemplatesContextProvider";
import type { ProcessContextProps } from "./Contexts/ProcessContext";
import { useProcessContext } from "./Contexts/ProcessContext";
import type { ProcessEditorSearchFilteredStep } from "./Contexts/ProcessSearchFilter";
import { useProcessSearchFilterContext } from "./Contexts/ProcessSearchFilter/ProcessSearchFilterContext";
import type { ProcessSearchFilter } from "./ListItems/ProcessListItemContextMenu";
import type { ProcessListLayoutLoaderLookupData } from "./ProcessListLayoutLoader";
import type { ProcessOverviewRootStep } from "./ProcessOverview/ProcessOverview";
import { ProcessOverview } from "./ProcessOverview/ProcessOverview";
import type { ProcessOverviewStepActionData, StepEnvironmentConfig, StepChannelConfig } from "./ProcessOverview/ProcessOverviewStepAction";
import TemplatedProjectOnboarding from "./TemplatedProjects/TemplatedProjectOnboarding";
class FilterLayout extends AdvancedFilterLayout<ProcessSearchFilter> {
}
export interface ProcessListProps {
    lookups: ProcessListLayoutLoaderLookupData;
    doBusyTask: DoBusyTask;
    location: Location;
    gitRefResource: GitRefResource | undefined;
    showStepNotes: boolean;
}
const ProcessList: React.FC<ProcessListProps> = ({ lookups, doBusyTask, location, gitRefResource, showStepNotes }) => {
    const processContext = useProcessContext();
    const { state: processContextState } = processContext;
    const processIdentifier = processContextState.processIdentifier;
    const processType = processContext.selectors.getProcessType();
    const processSearchFilterContext = useProcessSearchFilterContext();
    const actionTemplates = useActionTemplatesFromContext();
    const sampleProjectTourContext = useContext(SampleProjectTourContext);
    const processTemplates = useProcessTemplatesFromContext();
    useEffect(() => {
        if (sampleProjectTourContext !== undefined) {
            sampleProjectTourContext.continueSampleProjectTour();
        }
    }, [sampleProjectTourContext]);
    const [deploymentSettings, setDeploymentSettings] = useState<DeploymentSettingsResource | undefined>(undefined);
    useDoBusyTaskEffect(doBusyTask, async () => {
        const result = isDeploymentProcessIdentifier(processIdentifier) ? await repository.ProjectDeploymentSettings.get(processIdentifier.projectId, gitRefResource?.CanonicalName) : undefined;
        setDeploymentSettings(result);
    }, [processIdentifier, gitRefResource?.CanonicalName]);
    const { state: processSearchFilterContextLookupsState, actions: processSearchFilterContextActions } = processSearchFilterContext;
    const channelsById = lookups.channelsById;
    const hasValidProcess: boolean = processContext.selectors.hasValidProcess();
    const hasSteps: boolean = processContext.selectors.hasSteps();
    const convertedSteps = convertProcessSteps(processSearchFilterContext.filteredSteps.steps.filter((x) => x.filtered), processContext.selectors, actionTemplates, lookups, gitRefResource, processTemplates, processContext.state.processIdentifier);
    return (<>
            {hasValidProcess &&
            !hasSteps &&
            (isDeploymentProcessIdentifier(processIdentifier) ? (<Onboarding location={location} processIdentifier={processIdentifier} gitRef={gitRefResource?.CanonicalName}/>) : isProcessTemplateProcessIdentifier(processIdentifier) ? (<ProcessTemplateOnboarding slug={processIdentifier.slug} gitRef={gitRefResource?.CanonicalName}/>) : isProjectTemplateProcessIdentifier(processIdentifier) ? (<ProjectTemplateOnboarding slug={processIdentifier.slug} gitRef={processIdentifier.gitRef}/>) : isTemplatedProjectProcessIdentifier(processIdentifier) ? (<TemplatedProjectOnboarding projectTemplateSlug={processIdentifier.projectTemplateSlug} version={processIdentifier.version}/>) : (<RunbookOnboarding />))}
            {hasValidProcess && hasSteps && (<SampleProjectTourStep stepId={"DeploymentProcess"}>
                    {isDeploymentProcessIdentifier(processIdentifier) && deploymentSettings && <InvalidConfigurationCallouts processContext={processContext} deploymentSettings={deploymentSettings} processIdentifier={processIdentifier}/>}
                    <div className={processOverviewVNextStyles.processList}>{processSearchFilterContext.filteredSteps.steps.length > 0 ? <ProcessOverview steps={convertedSteps} showNote={showStepNotes}/> : <NoResults />}</div>
                </SampleProjectTourStep>)}
        </>);
};
ProcessList.displayName = "ProcessList"
function convertProcessSteps(filteredSteps: ProcessEditorSearchFilteredStep[], selectors: ProcessStateSelectors, actionTemplates: ActionTemplateSearchResource[], lookups: ProcessListLayoutLoaderLookupData, gitRefResource: GitRefResource | undefined, processTemplates: ProcessTemplateDetails[], processIdentifier: ProcessIdentifier): ProcessOverviewRootStep[] {
    function mapProcessTemplate(step: StoredStep, action: StoredAction): ProcessOverviewRootStep {
        const processTemplateVersionMask = getProcessTemplateVersionMaskFromAction(action);
        const processTemplateSlug = getProcessTemplateSlugFromAction(action);
        const processTemplate = processTemplates.find((b) => isMatchingFoundProcessTemplateDetails(b, processTemplateSlug, processTemplateVersionMask))?.processTemplate;
        if (processTemplate === undefined)
            return mapSingleStep(step, action);
        return {
            stepNumber: selectors.getStepNumber(step.Id),
            id: step.Id,
            name: action.Name,
            href: selectors.getStepDetailsUrl(gitRefResource, step.Id, action.Id),
            logo: { src: getActionLogoUrl(action) },
            logoTooltip: "Process Template",
            isDisabled: action.IsDisabled,
            summary: "Process Template",
            isRunInParallelWithPreviousStep: step.StartTrigger === StartTrigger.StartWithPrevious && !selectors.isFirstStep(step.Id),
            containerLabel: "Template",
            containerSteps: processTemplate.Steps.map((processTemplateStep, processTemplateStepIndex) => {
                if (processTemplateStep.Actions.length === 1) {
                    const processTemplateAction = processTemplateStep.Actions[0];
                    return {
                        ...mapCommonStepActionData(processTemplateAction),
                        href: getProcessTemplateStepDetailsUrl(processIdentifier, gitRefResource, action.Id),
                        isDisabled: processTemplateAction.IsDisabled || selectors.isActionDisabled(action.Id),
                        summary: <ProcessTemplateStepSummary step={processTemplateStep} action={processTemplateAction} lookups={lookups} isChildAction={false}/>,
                        isRunInParallelWithPreviousStep: processTemplateStep.StartTrigger === StartTrigger.StartWithPrevious && processTemplateStepIndex !== 0,
                    };
                }
                const maxParallelism = processTemplateStep.Properties[SpecialVariables.Action.MaxParallelism];
                const showWindowSize = typeof maxParallelism === "string" && maxParallelism ? maxParallelism.length > 0 : false;
                const processTemplateParentStepLogo = <StepRolling height="2.5rem" className={processOverviewVNextStyles.parentStepLogo}/>;
                const processTemplateParentStepTooltipContent = showWindowSize ? "Rolling deployment" : "Multi-step deployment across deployment targets";
                const summary = (<>
                        {processTemplateStep.Properties[SpecialVariables.Action.MaxParallelism] ? <span>Rolling deployment</span> : <span>Multi-step deployment</span>}
                        &nbsp;across deployment targets with&nbsp;
                        <Roles rolesAsCSV={processTemplateStep.Properties[SpecialVariables.Action.TargetRoles] as string}/>
                    </>);
                return {
                    id: processTemplateStep.Id,
                    name: processTemplateStep.Name,
                    href: getProcessTemplateStepDetailsUrl(processIdentifier, gitRefResource, action.Id),
                    logo: processTemplateParentStepLogo,
                    logoTooltip: processTemplateParentStepTooltipContent,
                    isDisabled: processTemplateStep.Actions.every((action: DeploymentActionResource) => action.IsDisabled) || selectors.isActionDisabled(action.Id),
                    summary,
                    isRunInParallelWithPreviousStep: processTemplateStep.StartTrigger === StartTrigger.StartWithPrevious && processTemplateStepIndex !== 0,
                    containerLabel: processTemplateStep.Properties[SpecialVariables.Action.MaxParallelism] ? "Rolling" : undefined,
                    containerSteps: processTemplateStep.Actions.map((processTemplateAction) => {
                        return {
                            ...mapCommonStepActionData(processTemplateAction),
                            href: getProcessTemplateStepDetailsUrl(processIdentifier, gitRefResource, action.Id),
                            isDisabled: processTemplateAction.IsDisabled || selectors.isActionDisabled(action.Id),
                            summary: <ProcessTemplateStepSummary step={processTemplateStep} action={processTemplateAction} lookups={lookups} isChildAction={true}/>,
                            isRunInParallelWithPreviousStep: false,
                        };
                    }),
                };
            }),
        };
    }
    function mapSingleStep(step: StoredStep, action: StoredAction): ProcessOverviewRootStep {
        const summary = action.plugin.summary(action.Properties, step.Properties[SpecialVariables.Action.TargetRoles] as string, action.Packages, action.WorkerPoolId ? lookups.workerPoolsById[action.WorkerPoolId] : undefined, action.WorkerPoolVariable ?? undefined, action.GitDependencies ?? undefined);
        return {
            ...mapCommonStepActionData(action),
            stepNumber: selectors.getStepNumber(step.Id),
            href: selectors.getStepDetailsUrl(gitRefResource, step.Id, action.Id),
            isDisabled: selectors.isActionDisabled(action.Id),
            summary,
            isRunInParallelWithPreviousStep: step.StartTrigger === StartTrigger.StartWithPrevious && !selectors.isFirstStep(step.Id),
        };
    }
    function mapParentStep(step: StoredStep): ProcessOverviewRootStep {
        const maxParallelism = step.Properties[SpecialVariables.Action.MaxParallelism];
        const showWindowSize = typeof maxParallelism === "string" && maxParallelism ? maxParallelism.length > 0 : false;
        // Previously, the tooltip content is a ReactNode that across two lines, but do not think it is necessary
        const logoTooltipContent = showWindowSize ? "Rolling deployment" : "Multi-step deployment across deployment targets";
        const summary = (<>
                {step.Properties[SpecialVariables.Action.MaxParallelism] ? <span>Rolling deployment</span> : <span>Multi-step deployment</span>}
                &nbsp;across deployment targets with&nbsp;
                <Roles rolesAsCSV={step.Properties[SpecialVariables.Action.TargetRoles] as string}/>
            </>);
        return {
            stepNumber: selectors.getStepNumber(step.Id),
            id: step.Id,
            name: step.Name,
            href: selectors.getStepDetailsUrl(gitRefResource, step.Id, null),
            logo: <StepRolling height="2.5rem" className={processOverviewVNextStyles.parentStepLogo}/>,
            logoTooltip: logoTooltipContent,
            isDisabled: selectors.isStepDisabled(step.Id),
            summary,
            isRunInParallelWithPreviousStep: step.StartTrigger === StartTrigger.StartWithPrevious && !selectors.isFirstStep(step.Id),
            containerLabel: step.Properties[SpecialVariables.Action.MaxParallelism] ? "Rolling" : undefined,
            containerSteps: selectors.getChildActions(step.Id).map((childAction) => mapChildStep(step, childAction)),
        };
    }
    function mapChildStep(step: StoredStep, childAction: StoredAction): ProcessOverviewStepActionData {
        const summary = childAction.plugin.summary(childAction.Properties, 
        // originally: isChildAction ? "" : (step.Properties[SpecialVariables.Action.TargetRoles] as string),
        "", childAction.Packages, childAction.WorkerPoolId ? lookups.workerPoolsById[childAction.WorkerPoolId] : undefined, childAction.WorkerPoolVariable ?? undefined, childAction.GitDependencies ?? undefined);
        return {
            ...mapCommonStepActionData(childAction),
            href: selectors.getStepDetailsUrl(gitRefResource, step.Id, childAction.Id),
            isDisabled: selectors.isActionDisabled(childAction.Id),
            summary,
            isRunInParallelWithPreviousStep: false,
        };
    }
    function mapCommonStepActionData(action: StoredAction | DeploymentActionResource) {
        return {
            id: action.Id,
            name: action.Name,
            logo: { src: getActionLogoUrl(action) },
            logoTooltip: actionTemplates.find((x) => x.Type === action.ActionType)?.Name ?? action.ActionType,
            environmentConfig: getEnvironmentConfig(action, lookups.environmentsV2ById),
            channelConfig: lookups.channelsById ? getChannelConfig(action, lookups.channelsById) : undefined,
            note: action.Notes === null ? undefined : <Markdown markup={action.Notes}/>,
        };
    }
    return filteredSteps.map(({ step }) => {
        if (step.ActionIds.length === 1) {
            const action = selectors.getActionById(step.ActionIds[0]);
            if (action.ActionType === "Octopus.ProcessTemplate") {
                return mapProcessTemplate(step, action);
            }
            return mapSingleStep(step, action);
        }
        else {
            return mapParentStep(step);
        }
    });
}
/**
 * In existing code, selectedEnvironments and skippedEnvironments can co-exist, but in our frontend, the control related to this field is a radio button,
 * so in reality, selectedEnvironments and skippedEnvironment is mutually exclusive
 * @param action
 * @param environmentsLookup
 */
function getEnvironmentConfig(action: StoredAction | DeploymentActionResource, environmentsLookup: ResourcesById<EnvironmentV2Resource>): StepEnvironmentConfig {
    const selectedEnvironmentIds = action.Environments;
    const skippedEnvironmentIds = action.ExcludedEnvironments;
    if (selectedEnvironmentIds.length > 0) {
        return {
            type: "selected",
            environmentNames: selectedEnvironmentIds.map((environmentId) => environmentsLookup[environmentId]?.Name).filter((item) => item),
        };
    }
    if (skippedEnvironmentIds.length > 0) {
        return {
            type: "skipped",
            environmentNames: skippedEnvironmentIds.map((environmentId) => environmentsLookup[environmentId]?.Name).filter((item) => item),
        };
    }
    return {
        type: "all",
    };
}
function getChannelConfig(action: StoredAction | DeploymentActionResource, channelsLookup: ResourcesById<ChannelResource>): StepChannelConfig | undefined {
    const hasChannels = action.Channels.length > 0;
    if (!hasChannels && !action.ChannelsVariable) {
        return undefined;
    }
    return {
        channelsLookup: hasChannels
            ? action.Channels.map((channelId) => ({
                Key: channelId,
                Channel: channelsLookup[channelId],
            }))
            : undefined,
        channelsVariable: action.ChannelsVariable,
    };
}
function getInvalidAutomaticReleaseCreationConfigurationCallout(processContext: ProcessContextProps, project: ProjectResource, processIdentifier: DeploymentProcessIdentifier) {
    if (project.AutoCreateRelease) {
        if (project.ReleaseCreationStrategy == null || project.ReleaseCreationStrategy.ReleaseCreationPackage == null) {
            return (<div>
                    This project is configured to use Automatic Release Creation, but the step is missing. Please adjust the{" "}
                    <InternalLink to={links.triggersPage.generateUrl({ spaceId: processIdentifier.spaceId, projectSlug: processIdentifier.projectSlug })}>Automatic Release Creation</InternalLink> configuration.
                </div>);
        }
        else {
            const action = flatten(processContext.selectors.getAllActions()).filter((a) => a.Name === project.ReleaseCreationStrategy.ReleaseCreationPackage.DeploymentAction);
            if (action && action.length > 0 && action[0].IsDisabled) {
                return (<div>
                        <span>
                            Step <InternalLink to={links.deploymentProcessEditorPage.generateUrl({ spaceId: processIdentifier.spaceId, projectSlug: processIdentifier.projectSlug }, { actionId: action[0].Id })}>{action[0].Name}</InternalLink> is
                            currently used for Automatic Release Creation, but it has been disabled.
                        </span>
                        <span>
                            Please re-enable the step, or adjust the <InternalLink to={links.triggersPage.generateUrl({ spaceId: processIdentifier.spaceId, projectSlug: processIdentifier.projectSlug })}>Automatic Release Creation</InternalLink>{" "}
                            configuration.
                        </span>
                    </div>);
            }
        }
    }
    return null;
}
const processOverviewVNextStyles = {
    processList: css({
        height: "100%",
        padding: `${space[24]} ${space[16]}`,
        backgroundSize: "52px 34px",
        backgroundColor: `rgb(from ${themeTokens.color.processWrapper.background.primary} r g b / 0.3)`,
        backgroundImage: `radial-gradient(2px circle at 13px 33px, ${themeTokens.color.processWrapper.background.secondary} 50%, transparent), radial-gradient(2px circle at 39px 17px, ${themeTokens.color.processWrapper.background.secondary} 50%, transparent)`,
    }),
    parentStepLogo: css({
        backgroundColor: colorScales.blue[600],
    }),
};
interface InvalidConfigurationCalloutsProps {
    processContext: ProcessContextProps;
    deploymentSettings: DeploymentSettingsResource;
    processIdentifier: DeploymentProcessIdentifier;
}
function InvalidConfigurationCallouts({ processContext, deploymentSettings, processIdentifier }: InvalidConfigurationCalloutsProps) {
    // Using projectContext here since these callouts are only ever shown on deployment processes
    const { state: { model: project }, } = useProjectContext();
    const arcCallout = getInvalidAutomaticReleaseCreationConfigurationCallout(processContext, project, processIdentifier);
    const actions = flatten(processContext.selectors.getAllActions());
    const versioningCallout = isVersioningConfigurationValid(project, deploymentSettings, actions);
    if (arcCallout || versioningCallout) {
        return (<UnstructuredFormSection stretchContent={true}>
                <Callout type={"warning"} title="Invalid Configuration">
                    {arcCallout}
                    {versioningCallout}
                </Callout>
            </UnstructuredFormSection>);
    }
    return null;
}
export default ProcessList;
